import React, { useState, useEffect } from "react";
import { Key, Save, MapPin, Plus, Trash2, FileText, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import { toast } from "sonner";

export const SettingsTab = () => {
  const [apiKeys, setApiKeys] = useState({});
  const [keyForm, setKeyForm] = useState({ FLW_PUBLIC_KEY: "", FLW_SECRET_KEY: "", FLW_ENCRYPTION_KEY: "" });
  const [siteSettings, setSiteSettings] = useState({ website_url: "", address: "", support_email: "", phone: "" });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [seoForm, setSeoForm] = useState({ template_key: "", name: "", description: "", variables: "{}" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [kRes, sRes, cRes] = await Promise.all([
        fetch(`${API}/admin/settings/api-keys`, { credentials: "include" }),
        fetch(`${API}/site-settings`),
        fetch(`${API}/admin/prompt-categories`, { credentials: "include" }),
      ]);
      if (kRes.ok) setApiKeys(await kRes.json());
      if (sRes.ok) setSiteSettings(await sRes.json());
      if (cRes.ok) setCategories(await cRes.json());
    } catch (e) {}
  };

  const saveApiKeys = async () => {
    const payload = Object.fromEntries(Object.entries(keyForm).filter(([, v]) => v.trim()));
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one new key"); return; }
    const res = await fetch(`${API}/admin/settings/api-keys`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Updated: ${data.updated.join(", ")} — active immediately, no redeploy needed`);
      setKeyForm({ FLW_PUBLIC_KEY: "", FLW_SECRET_KEY: "", FLW_ENCRYPTION_KEY: "" });
      fetchAll();
    } else toast.error("Failed to save keys");
  };

  const saveSiteSettings = async () => {
    const res = await fetch(`${API}/admin/site-settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(siteSettings),
    });
    if (res.ok) toast.success("Site settings saved");
    else toast.error("Failed to save");
  };

  const toggleMultilanguage = async (value) => {
    setSiteSettings(s => ({ ...s, multilanguage_enabled: value }));
    const res = await fetch(`${API}/admin/site-settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ ...siteSettings, multilanguage_enabled: value }),
    });
    if (res.ok) toast.success(value ? "Multi-language ENABLED — language switcher is now visible to users" : "Multi-language DISABLED — site locked to English");
    else toast.error("Failed to save");
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    const res = await fetch(`${API}/admin/prompt-categories`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ name: newCategory.trim() }),
    });
    const data = await res.json();
    if (res.ok) { toast.success("Category added"); setNewCategory(""); fetchAll(); }
    else toast.error(data.detail || "Failed");
  };

  const deleteCategory = async (id) => {
    const res = await fetch(`${API}/admin/prompt-categories/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Category removed"); fetchAll(); }
  };

  const addSeoTemplate = async () => {
    let variables = {};
    try { variables = JSON.parse(seoForm.variables || "{}"); } catch { toast.error("Variables must be valid JSON"); return; }
    const res = await fetch(`${API}/admin/seo/templates`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ ...seoForm, variables }),
    });
    const data = await res.json();
    if (res.ok) { toast.success(`SEO template "${data.template_key}" saved`); setSeoForm({ template_key: "", name: "", description: "", variables: "{}" }); }
    else toast.error(data.detail || "Failed");
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      {/* Multi-language toggle */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="multilanguage-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <Languages className="w-4 h-4 text-purple-400 mt-0.5" />
            <div>
              <h2 className="font-semibold">Multi-language UI</h2>
              <p className="text-[11px] text-white/40 mt-0.5">When ON, users see the language switcher (18 languages). When OFF, the site is English-only and the switcher is hidden.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${siteSettings.multilanguage_enabled ? "text-neon" : "text-white/40"}`}>{siteSettings.multilanguage_enabled ? "ON" : "OFF"}</span>
            <Switch checked={!!siteSettings.multilanguage_enabled} onCheckedChange={toggleMultilanguage} data-testid="multilanguage-toggle" />
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="api-keys-card">
        <div className="flex items-center gap-2 mb-1"><Key className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">API Keys (Flutterwave)</h2></div>
        <p className="text-[11px] text-white/40 mb-4">Stored encrypted in the database. Update anytime (e.g., after KYC approval) — no redeploy needed.</p>
        <div className="space-y-3">
          {["FLW_PUBLIC_KEY", "FLW_SECRET_KEY", "FLW_ENCRYPTION_KEY"].map(name => (
            <div key={name}>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-xs font-mono">{name}</Label>
                {apiKeys[name] && (
                  <>
                    <span className="text-[10px] text-white/30 font-mono">{apiKeys[name].masked}</span>
                    <Badge variant="outline" className={`text-[9px] ${apiKeys[name].source === "database" ? "border-neon/30 text-neon" : "border-white/20 text-white/40"}`}>{apiKeys[name].source}</Badge>
                  </>
                )}
              </div>
              <Input type="password" value={keyForm[name]} onChange={e => setKeyForm({ ...keyForm, [name]: e.target.value })} placeholder="Paste new key to update…" className="bg-dark-subtle border-white/10 text-sm font-mono" data-testid={`api-key-input-${name}`} />
            </div>
          ))}
          <Button onClick={saveApiKeys} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="save-api-keys"><Save className="w-3.5 h-3.5 mr-1" />Save Keys</Button>
        </div>
      </div>

      {/* Site Settings */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="site-settings-card">
        <div className="flex items-center gap-2 mb-4"><MapPin className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">Contact Information (shown on website)</h2></div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div><Label className="text-xs">Website URL</Label><Input value={siteSettings.website_url} onChange={e => setSiteSettings({ ...siteSettings, website_url: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" data-testid="site-website-input" /></div>
          <div><Label className="text-xs">Support Email</Label><Input value={siteSettings.support_email} onChange={e => setSiteSettings({ ...siteSettings, support_email: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" data-testid="site-email-input" /></div>
          <div><Label className="text-xs">Phone Number</Label><Input value={siteSettings.phone} onChange={e => setSiteSettings({ ...siteSettings, phone: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" data-testid="site-phone-input" /></div>
          <div><Label className="text-xs">Business Address</Label><Input value={siteSettings.address} onChange={e => setSiteSettings({ ...siteSettings, address: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" data-testid="site-address-input" /></div>
        </div>
        <Button onClick={saveSiteSettings} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="save-site-settings"><Save className="w-3.5 h-3.5 mr-1" />Save Contact Info</Button>
      </div>

      {/* Prompt Categories */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="categories-card">
        <div className="flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">Prompt Categories</h2></div>
        <div className="flex gap-2 mb-3">
          <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} onKeyDown={e => e.key === "Enter" && addCategory()} placeholder='e.g., "Medical"' className="bg-dark-subtle border-white/10 text-sm" data-testid="new-category-input" />
          <Button onClick={addCategory} className="bg-purple-700 text-white hover:bg-purple-600 text-xs whitespace-nowrap" data-testid="add-category-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => (
            <div key={c.category_id} className="flex items-center gap-1 bg-dark rounded-full border border-white/10 pl-3 pr-1 py-1" data-testid={`category-${c.category_id}`}>
              <span className="text-xs text-white/60">{c.name}</span>
              <button onClick={() => deleteCategory(c.category_id)} className="p-1 text-white/30 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-white/30 text-xs">No custom categories yet.</p>}
        </div>
      </div>

      {/* New SEO Template */}
      <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="seo-template-card">
        <div className="flex items-center gap-2 mb-4"><FileText className="w-4 h-4 text-purple-400" /><h2 className="font-semibold">Add SEO Template</h2></div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div><Label className="text-xs">Template Key</Label><Input value={seoForm.template_key} onChange={e => setSeoForm({ ...seoForm, template_key: e.target.value })} placeholder="e.g., local_seo" className="bg-dark-subtle border-white/10 text-sm font-mono" data-testid="seo-key-input" /></div>
          <div><Label className="text-xs">Name</Label><Input value={seoForm.name} onChange={e => setSeoForm({ ...seoForm, name: e.target.value })} placeholder="Local SEO Optimizer" className="bg-dark-subtle border-white/10 text-sm" data-testid="seo-name-input" /></div>
        </div>
        <div className="mb-3"><Label className="text-xs">Description</Label><Input value={seoForm.description} onChange={e => setSeoForm({ ...seoForm, description: e.target.value })} className="bg-dark-subtle border-white/10 text-sm" /></div>
        <div className="mb-3"><Label className="text-xs">Variables (JSON)</Label><Textarea value={seoForm.variables} onChange={e => setSeoForm({ ...seoForm, variables: e.target.value })} className="bg-dark-subtle border-white/10 text-xs font-mono min-h-[80px]" data-testid="seo-vars-input" /></div>
        <Button onClick={addSeoTemplate} disabled={!seoForm.template_key || !seoForm.name} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="save-seo-template"><Save className="w-3.5 h-3.5 mr-1" />Save SEO Template</Button>
      </div>
    </div>
  );
};

export default SettingsTab;
